import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Reveal, Eyebrow } from "./Reveal";
import { business, eventTypes, whatsappMessages, waLink } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", phone: "", email: "", event_type: "", event_date: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [date, setDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name";
    if (!/^[+\d][\d\s-]{6,}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.event_type) e.event_type = "Select an event type";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! Your consultation request has been received. We'll be in touch shortly.");
      setForm(emptyForm);
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-ivory py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid overflow-hidden rounded-3xl shadow-2xl lg:grid-cols-5">
          {/* Left — info (charcoal) */}
          <div className="bg-charcoal p-9 sm:p-12 lg:col-span-2">
            <Eyebrow dark>Get in Touch</Eyebrow>
            <h2 className="mt-6 font-display text-4xl leading-tight text-white">
              Let&apos;s plan your <span className="italic text-champagne">celebration.</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-white/60">
              Share a few details and our team will reach out to craft the perfect
              event with you.
            </p>

            <div className="mt-10 space-y-6">
              <a href={`tel:${business.phoneRaw}`} data-testid="contact-phone" className="group flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/30 group-hover:bg-champagne transition-colors duration-300">
                  <Phone className="h-5 w-5 text-champagne group-hover:text-charcoal" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/40">Phone</p>
                  <p className="text-white">{business.phone}</p>
                </div>
              </a>
              <a href={waLink(whatsappMessages.general)} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="group flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/30 group-hover:bg-champagne transition-colors duration-300">
                  <MessageCircle className="h-5 w-5 text-champagne group-hover:text-charcoal" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/40">WhatsApp</p>
                  <p className="text-white">{business.phone}</p>
                </div>
              </a>
              <a href={`mailto:${business.email}`} data-testid="contact-email" className="group flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/30 group-hover:bg-champagne transition-colors duration-300">
                  <Mail className="h-5 w-5 text-champagne group-hover:text-charcoal" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/40">Email</p>
                  <p className="text-white break-all">{business.email}</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/30">
                  <MapPin className="h-5 w-5 text-champagne" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/40">Office</p>
                  <p className="text-white/90 text-sm leading-relaxed">{business.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form (ivory) */}
          <div className="bg-ivory p-9 sm:p-12 lg:col-span-3">
            <form onSubmit={submit} data-testid="inquiry-form" className="grid gap-6 sm:grid-cols-2">
              <div>
                <input data-testid="input-name" className="elegant-input" placeholder="Full Name *"
                  value={form.name} onChange={(e) => set("name", e.target.value)} />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>
              <div>
                <input data-testid="input-phone" className="elegant-input" placeholder="Phone *"
                  value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
              </div>
              <div className="sm:col-span-2">
                <input data-testid="input-email" className="elegant-input" placeholder="Email *"
                  value={form.email} onChange={(e) => set("email", e.target.value)} />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>

              <div>
                <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                  <SelectTrigger data-testid="select-event-type"
                    className="elegant-input h-auto border-0 border-b border-champagne/40 rounded-none px-0.5 shadow-none focus:ring-0 text-charcoal data-[placeholder]:text-[#9a9186]">
                    <SelectValue placeholder="Event Type *" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`event-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.event_type && <p className="mt-1 text-xs text-red-600">{errors.event_type}</p>}
              </div>

              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="input-date"
                      className="elegant-input flex items-center justify-between text-left">
                      <span className={date ? "text-charcoal" : "text-[#9a9186]"}>
                        {date ? format(date, "PPP") : "Event Date"}
                      </span>
                      <CalendarIcon className="h-4 w-4 text-champagne" strokeWidth={1.5} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date}
                      onSelect={(d) => { setDate(d); set("event_date", d ? format(d, "PPP") : ""); }}
                      initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="sm:col-span-2">
                <textarea data-testid="input-message" rows={3} className="elegant-input resize-none"
                  placeholder="Tell us about your event"
                  value={form.message} onChange={(e) => set("message", e.target.value)} />
              </div>

              <div className="sm:col-span-2">
                <motion.button
                  data-testid="submit-inquiry-btn" type="submit" disabled={loading}
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-maroon py-4 text-sm font-medium uppercase tracking-wider text-white transition-colors duration-300 hover:bg-champagne hover:text-charcoal disabled:opacity-60">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loading ? "Sending..." : "Request Consultation"}
                </motion.button>
              </div>
            </form>
          </div>
        </div>

        {/* Map */}
        <Reveal delay={0.1} className="mt-8 overflow-hidden rounded-3xl border border-champagne/25 shadow-lg">
          <iframe
            data-testid="google-map"
            title="Aarambh.KD Office Location"
            src={business.mapEmbed}
            width="100%" height="360" style={{ border: 0 }}
            loading="lazy" referrerPolicy="no-referrer-when-downgrade"
          />
        </Reveal>
      </div>
    </section>
  );
};
